/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Children, startTransition, useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Box,
    HStack,
    Text,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

const DIRECTIONS = {
    FORWARD: 1,
    BACKWARD: -1
}

/**
 * SwatchGroup allows you to create a list of swatches
 * Each Swatch is a link with will direct to a href passed to them
 */
const SwatchGroup = (props) => {
    const {
        ariaLabel,
        children,
        label = '',
        value,
        pdpSwatches = null,
        handleChange = noop,
        groupProps = {},
        isShopTheLook,
        isRecommendation,
        showLabel
    } = props

    const styles = useStyleConfig('SwatchGroup')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [optimisticValue, setOptimisticValue] = useState(value)
    const wrapperRef = useRef(null)

    useEffect(() => {
        setOptimisticValue(value)
    }, [value])

    const onSelect = useCallback(
        (newValue, name) => {
            const isSwatchSelection = newValue !== undefined
            if (!isSwatchSelection) {
                startTransition(() => {
                    handleChange(newValue, true, name)
                })
                return
            }

            if (newValue === optimisticValue) return

            // Keep the selected ring and the product tile SKU in one interruptible update.
            // Rapid clicks can then replace unfinished renders without the ring and quick add
            // ever committing different variants.
            startTransition(() => {
                setOptimisticValue(newValue)
                handleChange(newValue, true, name)
            })
        },
        [handleChange, optimisticValue]
    )
    // Handle keyboard navigation.
    const onKeyDown = useCallback(
        (e) => {
            const {key} = e
            const move = (direction = DIRECTIONS.FORWARD) => {
                let index = selectedIndex + direction // forward = +1 backwards = -1
                index = (selectedIndex + direction) % children.length // keep number in bounds of array with modulus
                index = index < 0 ? children.length - Math.abs(index) : Math.abs(index) // We we are dealing with a negative we have to invert the index

                // Get a reference to the newly selected swatch as we are going to focus it later.
                const swatchEl = wrapperRef?.current?.children[index]

                // Set the new index that is always in the arrays range.
                setSelectedIndex(index)

                // Behave like a radio button a focus the new swatch.
                swatchEl?.focus()
            }

            switch (key) {
                case 'ArrowUp':
                case 'ArrowLeft':
                    e.preventDefault()
                    move(DIRECTIONS.BACKWARD)
                    break
                case 'ArrowDown':
                case 'ArrowRight':
                    e.preventDefault()
                    move(DIRECTIONS.FORWARD)
                    break
                default:
                    break
            }
        },
        [selectedIndex]
    )

    // Initialize the component state on mount, this includes the selected index value.
    useEffect(() => {
        if (!value) {
            return
        }
        const childrenArray = Children.toArray(children)
        const index = childrenArray.findIndex(({props}) => props?.value === value)

        setSelectedIndex(index)
    }, [])

    // Whenever the selected index changes ensure that we call the change handler.
    useEffect(() => {
        const childrenArray = Children.toArray(children)
        const newValue = childrenArray[selectedIndex]?.props.value
        handleChange(newValue)
    }, [selectedIndex])

    const textStyles = {
        color: 'black.100'
    }

    if (pdpSwatches?.indexOf(label) != -1) {
        textStyles.fontWeight = 300
        textStyles.letterSpacing = '0.065px'
        textStyles.fontSize = '13px'
    }

    return (
        <Box onKeyDown={onKeyDown} className="product-group-box">
            <Flex
                {...styles.swatchGroup}
                role="radiogroup"
                aria-label={ariaLabel || label}
                className="product-group-flex"
            >
                {label && pdpSwatches && (
                    <HStack {...styles.swatchLabel} mb={0} className="product-group-stack">
                        <Text {...textStyles} className="product-group-text">
                            <FormattedMessage
                                id="swatch_group.selected.color.label"
                                defaultMessage="{label}"
                                values={{label}}
                            />
                        </Text>
                    </HStack>
                )}
                {label &&
                    (isShopTheLook || isRecommendation) &&
                    showLabel &&
                    pdpSwatches == null && (
                        <HStack {...styles.swatchLabel} mb={0} className="product-group-stack">
                            <Text {...textStyles} fontSize="14px" className="product-group-text">
                                <FormattedMessage
                                    id="swatch_group.selected.color.label"
                                    defaultMessage="{label}"
                                    values={{label}}
                                />
                            </Text>
                        </HStack>
                    )}
                <Flex
                    ref={wrapperRef}
                    {...styles.swatchesWrapper}
                    {...groupProps}
                    className="product-group-swatches-wrapper"
                >
                    {Children.toArray(children).map((child) => {
                        const selected = child.props.value === optimisticValue
                        return React.cloneElement(child, {
                            onClick: () => onSelect(child.props.value, child.props.name),
                            selected
                        })
                    })}
                </Flex>
            </Flex>
        </Box>
    )
}

SwatchGroup.displayName = 'SwatchGroup'

SwatchGroup.propTypes = {
    /**
     * The aria label to be used on the group. If none is provided we will
     * use the label prop.
     */
    ariaLabel: PropTypes.string,
    /**
     * The attribute name of the swatch group. E.g color, size
     */
    label: PropTypes.string,
    /**
     * The display value of the selected option
     */
    displayName: PropTypes.string,
    /**
     * The Swatch options to choose between
     */
    children: PropTypes.node,
    /**
     * This function is called whenever the selected swatch changes.
     */
    handleChange: PropTypes.func,
    /**
     * The currentvalue for the option.
     */
    value: PropTypes.string,
    groupProps: PropTypes.object,
    pdpSwatches: PropTypes.any,
    isShopTheLook: PropTypes.bool,
    isRecommendation: PropTypes.bool,
    showLabel: PropTypes.bool
}

export default SwatchGroup
